import React, { useEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "redux/store";
import Table from "../../configandprint/_elements/Table";
import {
	CP_ACTIVITY_LOGS_PER_PAGE,
} from "redux/app/tool/constants";
import Td from "../../configandprint/_elements/Td";
import toolActions from "redux/app/tool/actions";
import toolSelectors from "redux/app/tool/selectors";
import moment from "moment";
import NumberedPagination from "components/_common/NumberedPagination";
import classNames from "classnames";
import ActivityLogCardView from "./ActivityLogCardView";
import { Popover2 } from "@blueprintjs/popover2";
import "@blueprintjs/popover2/lib/css/blueprint-popover2.css";
import {
	IActivityLogFilters,
	ICPActivityLog,
	ITableHeader,
} from "redux/app/tool/@types";

type TAuditColumnDef = {
	id: string;
	label: string;
	defaultVisible?: boolean;
	render: (activityLog: ICPActivityLog, index: number, page: number) => React.ReactNode;
};

const AUDIT_COLUMNS: TAuditColumnDef[] = [
	{
		id: "serial",
		label: "S.no",
		defaultVisible: true,
		render: (_activityLog, index, page) =>
			(page - 1) * CP_ACTIVITY_LOGS_PER_PAGE + index + 1,
	},
	{
		id: "vesselName",
		label: "Vessel Name",
		defaultVisible: true,
		render: (activityLog) => activityLog.vesselName || "NA",
	},
	{
		id: "module",
		label: "Module",
		defaultVisible: true,
		render: (activityLog) => activityLog.module || "NA",
	},
	{
		id: "code",
		label: "Code",
		defaultVisible: true,
		render: (activityLog) => activityLog.code || "NA",
	},
	{
		id: "action",
		label: "Action",
		defaultVisible: true,
		render: (activityLog) => activityLog.action || "NA",
	},
	{
		id: "message",
		label: "Change",
		defaultVisible: true,
		render: (activityLog) => activityLog.message || "NA",
	},
	{
		id: "version",
		label: "Version",
		defaultVisible: true,
		render: (activityLog) => activityLog.version || "NA",
	},
	{
		id: "userName",
		label: "Updated by",
		defaultVisible: true,
		render: (activityLog) => activityLog.userName || "NA",
	},
	{
		id: "timestampRelative",
		label: "Updated at",
		defaultVisible: true,
		render: (activityLog) =>
			activityLog.timestamp ? moment(activityLog.timestamp).fromNow() : "NA",
	},
	{
		id: "timestamp",
		label: "Timestamp (ISO)",
		render: (activityLog) => activityLog.timestamp || "NA",
	},
	{
		id: "id",
		label: "id",
		render: (activityLog) => activityLog.id || "NA",
	},
	{
		id: "_id",
		label: "_id",
		render: (activityLog) => activityLog._id || "NA",
	},
	{
		id: "_rev",
		label: "_rev",
		render: (activityLog) => activityLog._rev || "NA",
	},
	{
		id: "vesselId",
		label: "Vessel ID",
		render: (activityLog) => activityLog.vesselId || "NA",
	},
	{
		id: "userId",
		label: "User ID",
		render: (activityLog) => activityLog.userId || "NA",
	},
	{
		id: "modelName",
		label: "Model Name",
		render: (activityLog) => activityLog.modelName || "NA",
	},
	{
		id: "entityType",
		label: "Entity Type",
		render: (activityLog) => activityLog.entityType || "NA",
	},
	{
		id: "entityId",
		label: "Entity ID",
		render: (activityLog) => activityLog.entityId || "NA",
	},
	{
		id: "totalChanges",
		label: "Total Changes",
		render: (activityLog) => activityLog.totalChanges ?? "NA",
	},
	{
		id: "changes",
		label: "Changes",
		render: (activityLog) => {
			if (!activityLog.changes?.length) return "NA";
			const fullValue = JSON.stringify(activityLog.changes);
			const shortValue =
				fullValue.length > 180 ? `${fullValue.slice(0, 180)}...` : fullValue;
			return <span title={fullValue}>{shortValue}</span>;
		},
	},
	{
		id: "metadata",
		label: "Metadata",
		render: (activityLog) => {
			if (!activityLog.metadata) return "NA";
			const fullValue = JSON.stringify(activityLog.metadata);
			const shortValue =
				fullValue.length > 180 ? `${fullValue.slice(0, 180)}...` : fullValue;
			return <span title={fullValue}>{shortValue}</span>;
		},
	},
];

const DEFAULT_VISIBLE_COLUMN_IDS = AUDIT_COLUMNS.filter(
	(column) => column.defaultVisible
).map((column) => column.id);

const ALL_AUDIT_MODULE_OPTIONS = [
	"config",
	"print",
	"feature-flags",
	"faq",
	"groups",
	"ranges",
	"idl-events",
	"tenants",
	"releases",
	"apply-cnr-patches",
	"create-bunker-reports",
	"delete-old-cnr-docs",
	"users",
	"feedback",
	"cnr-tools",
	"cnr-ranges",
	"forms",
	"signalReports",
];

type OwnProps = {};
type ToolCPActivityLogProps = PropsFromRedux & OwnProps;
function ToolCPActivityLog(props: ToolCPActivityLogProps) {
	const {
		activityLogs,
		jobToLoadCPActivityLogs,
		paginationOfCPActivityLogs,
		jobToLoadCount,
	} = props;

	// card & table
	const [isCardView, setIsCardView] = useState(true);
	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
	const [draftFilters, setDraftFilters] = useState({
		module: "",
		userId: "",
		vesselId: "",
		startDate: "",
		endDate: "",
	});
	const [appliedFilters, setAppliedFilters] = useState<IActivityLogFilters>({});
	const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(
		DEFAULT_VISIBLE_COLUMN_IDS
	);

	const visibleColumns = AUDIT_COLUMNS.filter((column) =>
		visibleColumnIds.includes(column.id)
	);
	const tableHeaders: ITableHeader[] = visibleColumns.map((column) => ({
		name: column.label,
	}));

	useEffect(() => {
		props.loadAllVesselsForFilter();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isCardView) props.loadCountForList(appliedFilters);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCardView, appliedFilters]);

	useEffect(() => {
		if (!isCardView) {
			props.loadActivityLogForConfigAndPrints(false, appliedFilters);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paginationOfCPActivityLogs.currentPage, isCardView, appliedFilters]);

	const moduleOptions = Array.from(
		new Set([
			...ALL_AUDIT_MODULE_OPTIONS,
			...activityLogs.ids
				.map((id) => activityLogs.byIds[id]?.module)
				.filter((value) => !!value),
		])
	).sort((firstModule, secondModule) => firstModule.localeCompare(secondModule));
	const userOptions = Array.from(
		new Set(
			activityLogs.ids
				.map((id) => {
					const logEntry = activityLogs.byIds[id];
					if (!logEntry?.userId) return "";
					return JSON.stringify({
						id: logEntry.userId,
						name: logEntry.userName || logEntry.userId,
					});
				})
				.filter((value) => !!value)
		)
	)
		.map((value) => JSON.parse(value))
		.sort((firstUser, secondUser) => firstUser.name.localeCompare(secondUser.name));
	const vesselOptions = props.vessels.ids
		.map((vesselId) => props.vessels.byIds[vesselId])
		.filter((vessel) => !!vessel?.id && !!vessel?.vesselName)
		.sort((firstVessel, secondVessel) =>
			firstVessel.vesselName.localeCompare(secondVessel.vesselName)
		);

	const applyFilters = () => {
		const nextFilters: IActivityLogFilters = {};
		if (draftFilters.module.trim()) nextFilters.module = draftFilters.module.trim();
		if (draftFilters.userId) nextFilters.userId = draftFilters.userId;
		if (draftFilters.vesselId) nextFilters.vesselId = draftFilters.vesselId;
		if (draftFilters.startDate) {
			nextFilters.startDate = new Date(
				`${draftFilters.startDate}T00:00:00.000Z`
			).toISOString();
		}
		if (draftFilters.endDate) {
			nextFilters.endDate = new Date(
				`${draftFilters.endDate}T23:59:59.999Z`
			).toISOString();
		}
		setAppliedFilters(nextFilters);
		props.goToNewPage(1);
	};

	const clearFilters = () => {
		setDraftFilters({
			module: "",
			userId: "",
			vesselId: "",
			startDate: "",
			endDate: "",
		});
		setAppliedFilters({});
		props.goToNewPage(1);
	};

	const toggleColumnVisibility = (columnId: string) => {
		setVisibleColumnIds((currentColumnIds) => {
			if (currentColumnIds.includes(columnId)) {
				if (currentColumnIds.length === 1) {
					return currentColumnIds;
				}
				return currentColumnIds.filter((id) => id !== columnId);
			}
			return [...currentColumnIds, columnId];
		});
	};

	const getColumnSettingsUI = () => (
		<div className="w-72 rounded-md border border-gray-200 bg-white shadow-sm">
			<div className="px-3 pt-3 pb-2 border-b border-gray-100">
				<div className="flex items-center justify-between">
					<span className="text-xs font-semibold text-gray-800">Table Columns</span>
					<span className="text-[11px] text-gray-500">
						{visibleColumnIds.length}/{AUDIT_COLUMNS.length}
					</span>
				</div>
				<div className="mt-2 flex items-center gap-2">
					<button
						className="text-[11px] px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
						onClick={() => setVisibleColumnIds(AUDIT_COLUMNS.map((column) => column.id))}
					>
						Show all
					</button>
					<button
						className="text-[11px] px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
						onClick={() =>
							setVisibleColumnIds([DEFAULT_VISIBLE_COLUMN_IDS[0] || AUDIT_COLUMNS[0].id])
						}
					>
						Hide all
					</button>
					<button
						className="ml-auto text-[11px] text-blue-600 hover:text-blue-700"
						onClick={() => setVisibleColumnIds(DEFAULT_VISIBLE_COLUMN_IDS)}
					>
						Reset
					</button>
				</div>
			</div>
			<div className="p-2 max-h-80 overflow-y-auto ws-scroll">
				{AUDIT_COLUMNS.map((column) => (
					<label
						key={column.id}
						className="flex items-center gap-2 px-2 py-1.5 text-[12px] text-gray-700 cursor-pointer rounded hover:bg-gray-50"
					>
						<input
							type="checkbox"
							checked={visibleColumnIds.includes(column.id)}
							onChange={() => toggleColumnVisibility(column.id)}
							className="h-3.5 w-3.5 cursor-pointer accent-[#137CBD]"
						/>
						<span className="leading-4">{column.label}</span>
					</label>
				))}
			</div>
		</div>
	);

	return (
		<div className="bg-white  m-2 rounded-md overflow-y-auto">
			<div className="px-4 mb-4 overflow-y-auto">
				<div className="flex justify-between items-center">
					<h2 className="flex items-center py-2 my-2 space-x-2 text-xl font-medium ">
						<span
							className={classNames(
								"bp3-icon bp3-icon-arrow-left text-xl font-normal cursor-pointer"
							)}
							onClick={() => props.goBack()}
						/>
						<span>Audit Log</span>
					</h2>
					<div className="p-0">
						<div className="border-gray-200">
							<div className="flex items-center gap-2 py-1 my-2">
								<div className="flex flex-row justify-around rounded-md bg-lightGray">
									<button
										className={classNames(
											"transition-all duration-300 px-10 text-[12px]",
											{
												"p-2 flex-1 mx-1 font-medium text-gray-800 bg-white rounded-md shadow-sm outline-none":
													isCardView,
												"p-2 flex-1 mx-1 font-medium text-gray-500 rounded-md outline-none hover:text-gray-700":
													!isCardView,
											}
										)}
										onClick={() => {
											setIsCardView(true);
											props.resetCardActivityLogList();
											props.resetCurrentPageForCardActivityLogList();
										}}
									>
										Card
									</button>
									<button
										className={classNames(
											"transition-all duration-300 px-10 text-[12px]",
											{
												"py-1.5 flex-1 mx-1 font-medium text-gray-800 bg-white rounded-md shadow-sm outline-none":
													!isCardView,
												"py-1.5 flex-1 mx-1 font-medium text-gray-500 rounded-md outline-none hover:text-gray-700":
													isCardView,
											}
										)}
										onClick={() => setIsCardView(false)}
									>
										Table
									</button>
								</div>
								{!isCardView ? (
									<Popover2
										content={getColumnSettingsUI()}
										placement="bottom-end"
										minimal
									>
										<button className="text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-600 hover:text-gray-800 hover:border-gray-400">
											<span className="bp3-icon bp3-icon-cog mr-1" />
											Columns
										</button>
									</Popover2>
								) : null}
							</div>
						</div>
					</div>
				</div>
				{isCardView ? (
					<ActivityLogCardView isCardView={isCardView} />
				) : (
					<>
						<div className="mb-3">
							<button
								className="h-8 text-xs px-3 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
								onClick={() => setIsFilterPanelOpen((isOpen) => !isOpen)}
							>
								<span
									className={classNames(
										"bp3-icon mr-1",
										isFilterPanelOpen
											? "bp3-icon-chevron-up"
											: "bp3-icon-filter"
									)}
								/>
								{isFilterPanelOpen ? "Hide Filters" : "Show Filters"}
							</button>
						</div>
						{isFilterPanelOpen ? (
							<div className="mb-3 p-3 rounded-md border border-gray-200 bg-gray-50">
							<div className="flex items-center justify-between mb-2">
								<h4 className="text-xs font-semibold text-gray-700">Filters</h4>
								<div className="flex items-center gap-2">
									<button
										className="h-8 text-xs px-3 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
										onClick={clearFilters}
									>
										Clear
									</button>
									<button
										className="h-8 text-xs px-3 rounded bg-blue-600 text-white hover:bg-blue-700"
										onClick={applyFilters}
									>
										Apply
									</button>
								</div>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
								<div className="md:col-span-2">
									<label className="block text-[11px] text-gray-600 mb-1">
										Module
									</label>
									<select
										value={draftFilters.module}
										onChange={(event) =>
											setDraftFilters((prev) => ({
												...prev,
												module: event.target.value,
											}))
										}
										className="h-8 w-full text-xs px-2 border border-gray-300 rounded bg-white"
									>
										<option value="">All modules</option>
										{moduleOptions.map((value) => (
											<option value={value} key={value}>
												{value}
											</option>
										))}
									</select>
								</div>
								<div className="md:col-span-3">
									<label className="block text-[11px] text-gray-600 mb-1">
										User
									</label>
									<select
										value={draftFilters.userId}
										onChange={(event) =>
											setDraftFilters((prev) => ({
												...prev,
												userId: event.target.value,
											}))
										}
										className="h-8 w-full text-xs px-2 border border-gray-300 rounded bg-white"
									>
										<option value="">All users</option>
										{userOptions.map((user) => (
											<option value={user.id} key={user.id}>
												{user.name}
											</option>
										))}
									</select>
								</div>
								<div className="md:col-span-3">
									<label className="block text-[11px] text-gray-600 mb-1">
										Vessel
									</label>
									<select
										value={draftFilters.vesselId}
										onChange={(event) =>
											setDraftFilters((prev) => ({
												...prev,
												vesselId: event.target.value,
											}))
										}
										className="h-8 w-full text-xs px-2 border border-gray-300 rounded bg-white"
									>
										<option value="">All vessels</option>
										{vesselOptions.map((vessel) => (
											<option value={vessel.id} key={vessel.id}>
												{vessel.vesselName}
											</option>
										))}
									</select>
								</div>
								<div className="md:col-span-2">
									<label className="block text-[11px] text-gray-600 mb-1">
										From
									</label>
									<input
										type="date"
										value={draftFilters.startDate}
										onChange={(event) =>
											setDraftFilters((prev) => ({
												...prev,
												startDate: event.target.value,
											}))
										}
										className="h-8 w-full text-xs px-2 border border-gray-300 rounded bg-white"
									/>
								</div>
								<div className="md:col-span-2">
									<label className="block text-[11px] text-gray-600 mb-1">
										To
									</label>
									<input
										type="date"
										value={draftFilters.endDate}
										onChange={(event) =>
											setDraftFilters((prev) => ({
												...prev,
												endDate: event.target.value,
											}))
										}
										className="h-8 w-full text-xs px-2 border border-gray-300 rounded bg-white"
									/>
								</div>
							</div>
						</div>
						) : null}
						<div className="overflow-x-auto">
							<Table
								isLoading={
									jobToLoadCPActivityLogs.isLoading || jobToLoadCount.isLoading
								}
								isEmpty={
									!jobToLoadCPActivityLogs.isLoading &&
									!activityLogs?.ids?.length
								}
								headers={tableHeaders}
							>
								{activityLogs.ids.map((activityLogId, index) => {
									const activityLog = activityLogs.byIds[activityLogId];
									return (
										<tr key={activityLogId}>
											{visibleColumns.map((column) => (
												<Td className="py-2.5" key={`${activityLogId}-${column.id}`}>
													{column.render(
														activityLog,
														index,
														paginationOfCPActivityLogs.currentPage
													)}
												</Td>
											))}
										</tr>
									);
								})}
							</Table>
						</div>

						{activityLogs?.ids?.length > 0 ? (
							<NumberedPagination
								{...{
									...paginationOfCPActivityLogs,
									totalItems: paginationOfCPActivityLogs.totalItems || 0,
								}}
								itemIds={[]}
								onPageChange={function onPageChange(pageNumberFromPagination) {
									props.goToNewPage(pageNumberFromPagination);
								}}
								isPageLoading={
									jobToLoadCPActivityLogs.isLoading || jobToLoadCount.isLoading
								}
							/>
						) : null}
					</>
				)}
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const toolStore = toolSelectors._getStore(store);
	const activityLogs = toolSelectors.getCPActivityLog(toolStore);
	const jobToLoadCPActivityLogs =
		toolSelectors.jobToLoadCPActivityLogs(toolStore);
	const paginationOfCPActivityLogs =
		toolSelectors.getPaginationOfCPActivityLog(toolStore);

	const jobToLoadCount = toolSelectors.jobToLoadCount(toolStore);

	return {
		activityLogs,
		jobToLoadCPActivityLogs,
		paginationOfCPActivityLogs,
		jobToLoadCount,
		vessels: toolSelectors.getVessels(toolStore),
	};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {
		loadAllVesselsForFilter() {
			dispatch(toolActions.commands.toolTenantSpecificVesselLoad(""));
		},
		loadActivityLogForConfigAndPrints(
			isCardView: boolean,
			filters?: IActivityLogFilters
		) {
			dispatch(toolActions.commands.toolCPActivityLogLoad(isCardView, filters));
		},
		goToNewPage(pageNumber: number) {
			dispatch(
				toolActions.document.toolCPActivityLogPaginationCurrentPageSet(
					pageNumber
				)
			);
		},
		resetCardActivityLogList() {
			dispatch(toolActions.document.toolCPCardActivityLogListReset());
		},
		resetCurrentPageForCardActivityLogList() {
			dispatch(
				toolActions.document.toolCPCardActivityLogPaginationCurrentPageSet(1)
			);
		},
		loadCountForList(filters?: IActivityLogFilters) {
			dispatch(
				toolActions.commands.toolCountListLoad(
					"activitylog",
					"cp-activity-log",
					filters
				)
			);
		},
		goBack() {
			window?.history?.back();
		},
	};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(ToolCPActivityLog);
